import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Compass, Radar as RadarIcon, Trophy } from "lucide-react";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const API = `${BACKEND_URL}/api`;

const REFRESH_MS = 30000;
const SCAN_RADIUS_M = 5;
const RADAR_MAX_RANGE_M = 800;   // anything past this clamps to the edge

// Proximity ping. The whole mechanic is "walk until the number gets small",
// which asks people to stare at a phone while moving. An audible pulse that
// quickens and rises in pitch as the target closes lets them look up instead.
const PING_MAX_RANGE_M = 1200;   // beyond this there's nothing useful to say
const PING_SLOW_MS = 2600;       // gap at the edge of range
const PING_FAST_MS = 260;        // gap at the scan radius
const PING_LOW_HZ = 620;
const PING_HIGH_HZ = 1560;

// Live distance/bearing from the current GPS fix. The server computes these
// when the list is fetched, which is only every 30 s — far too coarse for a
// cue that's meant to respond to walking. The set of traces still comes from
// the server; only the geometry is recomputed here.
const EARTH_R_M = 6371000;
const toRad = (d) => (d * Math.PI) / 180;

const distanceM = (a, b) => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const la = toRad(a.lat);
  const lb = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la) * Math.cos(lb) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R_M * Math.asin(Math.min(1, Math.sqrt(h)));
};

const bearingDeg = (a, b) => {
  const la = toRad(a.lat);
  const lb = toRad(b.lat);
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(lb);
  const x = Math.cos(la) * Math.sin(lb) - Math.sin(la) * Math.cos(lb) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
};

const CARDINAL = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const cardinal = (deg) => CARDINAL[Math.round(((deg % 360) + 360) % 360 / 45) % 8];

const instructionFor = (relDeg) => {
  // relDeg is "where the target is relative to where you're facing"
  // (target_bearing - device_heading), normalised to (-180, 180]
  const a = ((relDeg + 540) % 360) - 180;
  const abs = Math.abs(a);
  if (abs < 22) return "Continue ahead";
  if (abs < 67) return a > 0 ? "Bear slightly right" : "Bear slightly left";
  if (abs < 112) return a > 0 ? "Turn right" : "Turn left";
  if (abs < 157) return a > 0 ? "Turn sharply right" : "Turn sharply left";
  return "Turn around";
};

const formatDistance = (m) => {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
};

/**
 * Half-radar: top-down arc covering the 180° in front of the device.
 * Targets are placed at angle relative to device heading (so straight up = ahead).
 * Distance is squashed onto the radius with a sqrt curve so close targets are
 * clearly readable while still showing far ones near the rim.
 */
const HalfRadar = ({ traces, heading, onSelect, selectedId }) => {
  // SVG is 320×200; centre at (160, 180); radius 150 — leaves a bit of room
  // below the centre so the "YOU" label stays inside the viewBox.
  const cx = 160, cy = 180, r = 150;

  const dots = useMemo(() => {
    return traces.map((t) => {
      const rel = ((t.bearing - heading) + 540) % 360 - 180; // [-180..180]
      // Only the forward 180° arc fits on a half-radar — others are listed
      // as "Turn around" in the trace list below.
      const inFront = Math.abs(rel) <= 90;
      if (!inFront) return { ...t, inFront, x: null, y: null };
      const dist = Math.min(t.distance_m, RADAR_MAX_RANGE_M);
      const scale = Math.sqrt(dist / RADAR_MAX_RANGE_M); // 0..1, gentle curve
      const radiusPx = Math.max(8, scale * (r - 6));      // keep inside rim
      // Convert relative bearing to SVG angle (0° = up = -90° in SVG)
      const theta = (rel - 90) * Math.PI / 180;
      const x = cx + radiusPx * Math.cos(theta);
      const y = cy + radiusPx * Math.sin(theta);
      return { ...t, rel, inFront, x, y };
    });
  }, [traces, heading]);

  return (
    <svg
      className="trace-radar"
      viewBox="0 0 320 200"
      data-testid="trace-radar"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <radialGradient id="radar-sweep" cx="50%" cy="100%" r="100%">
          <stop offset="0%" stopColor="rgba(239,68,68,0.45)" />
          <stop offset="60%" stopColor="rgba(239,68,68,0.06)" />
          <stop offset="100%" stopColor="rgba(239,68,68,0)" />
        </radialGradient>
        <linearGradient id="radar-line" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(239,68,68,0)" />
          <stop offset="100%" stopColor="rgba(239,68,68,0.8)" />
        </linearGradient>
      </defs>

      {/* Range rings */}
      <g>
        {[0.33, 0.66, 1].map((f, i) => (
          <path
            key={i}
            d={`M ${cx - r * f} ${cy} A ${r * f} ${r * f} 0 0 1 ${cx + r * f} ${cy}`}
            fill="none"
            stroke="rgba(239,68,68,0.25)"
            strokeWidth="1"
            strokeDasharray={i === 2 ? "0" : "2,4"}
          />
        ))}
      </g>

      {/* Cross-hair lines */}
      <line x1={cx} y1={cy} x2={cx} y2={cy - r} stroke="rgba(239,68,68,0.25)" strokeWidth="1" />
      <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} stroke="rgba(239,68,68,0.25)" strokeWidth="1" />
      <line x1={cx - r * 0.7} y1={cy - r * 0.7} x2={cx + r * 0.7} y2={cy - r * 0.7} stroke="rgba(239,68,68,0.1)" strokeWidth="1" />

      {/* Sweeping arm — pure CSS rotation */}
      <g className="radar-sweep-group" style={{ transformOrigin: `${cx}px ${cy}px` }}>
        <path
          d={`M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r * Math.sin(0.6)} ${cy - r * Math.cos(0.6)} Z`}
          fill="url(#radar-sweep)"
        />
        <line x1={cx} y1={cy} x2={cx} y2={cy - r} stroke="url(#radar-line)" strokeWidth="1.4" />
      </g>

      {/* Centre marker — the user */}
      <circle cx={cx} cy={cy} r="3" fill="#ef4444" />
      <text x={cx} y={cy + 14} textAnchor="middle"
            fontSize="8" fontFamily="ui-monospace, monospace" fill="rgba(239,68,68,0.7)"
            letterSpacing="2">YOU</text>

      {/* Behind-the-user indicator — small arrow at the bottom edge */}
      {dots.some((d) => !d.inFront) && (
        <g data-testid="trace-blip-behind-indicator" opacity="0.7">
          <path d={`M ${cx - 6} ${cy + 6} L ${cx + 6} ${cy + 6} L ${cx} ${cy + 16} Z`}
                fill="#ef4444" />
        </g>
      )}
    </svg>
  );
};

/**
 * HTML blip overlay — rendered on top of the SVG radar because Emergent's
 * dev tooling wraps dynamic SVG content with a `<span data-ve-dynamic>` that
 * breaks SVG rendering for `<g>` arrays. HTML divs render reliably.
 */
const RadarBlipsOverlay = ({ traces, heading, selectedId, onSelect }) => {
  // Match HalfRadar's geometry exactly so blips line up with the SVG rings.
  const cxPct = 50;      // center X as % of overlay width
  const cyPct = 90;      // center Y as % of overlay height (180/200 = 90%)
  const rPct = 75;       // radius as % of overlay width (150/(320/2)/2 = 75/100)
  return (
    <div className="trace-blip-layer" data-testid="trace-blip-layer" aria-hidden="true">
      {traces.map((t) => {
        const rel = ((t.bearing - heading) + 540) % 360 - 180;
        if (Math.abs(rel) > 90) return null;
        const dist = Math.min(t.distance_m, RADAR_MAX_RANGE_M);
        const scale = Math.sqrt(dist / RADAR_MAX_RANGE_M);
        const radius = Math.max(0.05, scale) * rPct;
        const theta = (rel - 90) * Math.PI / 180;
        const x = cxPct + radius * Math.cos(theta);
        const y = cyPct + radius * Math.sin(theta);
        const distLabel =
          t.distance_m < 1000
            ? `${Math.round(t.distance_m)}m`
            : `${(t.distance_m / 1000).toFixed(1)}km`;
        return (
          <button
            key={t.id}
            className={`trace-blip-dom ${selectedId === t.id ? "selected" : ""}`}
            style={{ left: `${x}%`, top: `${y}%` }}
            data-testid={`trace-blip-${t.id}`}
            onClick={() => onSelect(t.id)}
            aria-label={`${t.agent_code} ${distLabel}`}
          >
            <span className="trace-blip-dot" />
            <span className="trace-blip-pulse" />
            <span className="trace-blip-label">{distLabel}</span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * Trace Tracker full-screen overlay. Uses real GPS + DeviceOrientationEvent.
 */
export const TraceTracker = ({ open, onClose, userLocation, watcherId, onScanned, soundEnabled = true }) => {
  const [traces, setTraces] = useState([]);
  const [heading, setHeading] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [scansCount, setScansCount] = useState(0);
  const [orientationStatus, setOrientationStatus] = useState("unknown"); // unknown | granted | denied | unavailable
  const [scanState, setScanState] = useState(null); // { traceId, startedAt, durationMs }
  // 0..1, advanced by the rAF loop below. Held in state (not derived at render
  // from Date.now()) because nothing else re-renders during a hold — the ring
  // used to sit frozen at zero for the whole scan.
  const [scanProgress, setScanProgress] = useState(0);
  const scanRafRef = useRef(null);
  const lastRefreshRef = useRef(0);

  // Pull nearest 3 traces every 30 s, plus immediate refresh on open
  useEffect(() => {
    if (!open || !userLocation) return undefined;
    const fetchNearby = async () => {
      try {
        const r = await axios.get(
          `${API}/traces/nearby?lat=${userLocation.lat}&lng=${userLocation.lng}&limit=3`
        );
        setTraces(r.data.traces || []);
        lastRefreshRef.current = Date.now();
      } catch {}
    };
    fetchNearby();
    const id = setInterval(fetchNearby, REFRESH_MS);
    return () => clearInterval(id);
  }, [open, userLocation]);

  // Personal scan counter
  useEffect(() => {
    if (!open || !watcherId) return undefined;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/scans-count?watcher_id=${watcherId}`);
        setScansCount(r.data.count || 0);
      } catch {}
    };
    fetchCount();
    const id = setInterval(fetchCount, 5000);
    return () => clearInterval(id);
  }, [open, watcherId]);

  // Phone rotation → heading
  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === "undefined" || !("DeviceOrientationEvent" in window)) {
      setOrientationStatus("unavailable");
      return undefined;
    }
    const handler = (e) => {
      let h;
      if (typeof e.webkitCompassHeading === "number") {
        h = e.webkitCompassHeading;            // iOS: already 0..360 clockwise from N
      } else if (typeof e.alpha === "number") {
        h = (360 - e.alpha) % 360;             // Generic: convert anti-clockwise alpha
      } else {
        return;
      }
      setHeading(h);
    };
    const needsPermission =
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function";
    let cleanup = null;
    if (needsPermission) {
      // iOS requires a user gesture; we'll attach a one-shot listener on the
      // "Request compass" button. Until then, status stays "unknown".
    } else {
      window.addEventListener("deviceorientationabsolute", handler, true);
      window.addEventListener("deviceorientation", handler, true);
      setOrientationStatus("granted");
      cleanup = () => {
        window.removeEventListener("deviceorientationabsolute", handler, true);
        window.removeEventListener("deviceorientation", handler, true);
      };
    }
    return cleanup || undefined;
  }, [open]);

  const requestCompass = async () => {
    try {
      const state = await DeviceOrientationEvent.requestPermission();
      if (state === "granted") {
        setOrientationStatus("granted");
        window.addEventListener("deviceorientation", (e) => {
          let h;
          if (typeof e.webkitCompassHeading === "number") h = e.webkitCompassHeading;
          else if (typeof e.alpha === "number") h = (360 - e.alpha) % 360;
          else return;
          setHeading(h);
        }, true);
      } else {
        setOrientationStatus("denied");
      }
    } catch {
      setOrientationStatus("denied");
    }
  };

  // Everything the screen draws works off live geometry, so the radar, the
  // instructions and the in-range check all track the GPS rather than the last
  // 30-second fetch.
  const liveTraces = useMemo(() => {
    if (!userLocation) return traces;
    return traces
      .map((t) => ({
        ...t,
        distance_m: distanceM(userLocation, t),
        bearing: bearingDeg(userLocation, t),
      }))
      .sort((a, b) => a.distance_m - b.distance_m);
  }, [traces, userLocation]);

  const nearestDistance = liveTraces.length ? liveTraces[0].distance_m : Infinity;

  // The ping loop reschedules itself off this ref rather than depending on the
  // distance directly — a dependency that changes on every GPS fix would tear
  // the timer down and rebuild it before it ever fired.
  const pingDistRef = useRef(Infinity);
  useEffect(() => { pingDistRef.current = nearestDistance; }, [nearestDistance]);

  const audioCtxRef = useRef(null);
  const playPing = useCallback((closeness) => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(PING_LOW_HZ + closeness * (PING_HIGH_HZ - PING_LOW_HZ), t);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.05 + closeness * 0.05, t + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.13);
    } catch { /* no audio available — the meter still reads */ }
  }, []);

  useEffect(() => {
    if (!open || !soundEnabled) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = () => {
      if (cancelled) return;
      const d = pingDistRef.current;
      if (d > PING_MAX_RANGE_M) { timer = setTimeout(tick, PING_SLOW_MS); return; }
      // 0 at the edge of range, 1 at the scan radius.
      const span = PING_MAX_RANGE_M - SCAN_RADIUS_M;
      const closeness = Math.min(1, Math.max(0, 1 - (d - SCAN_RADIUS_M) / span));
      playPing(closeness);
      if (navigator.vibrate) {
        try { navigator.vibrate(Math.round(10 + closeness * 25)); } catch {}
      }
      timer = setTimeout(tick, PING_SLOW_MS - closeness * (PING_SLOW_MS - PING_FAST_MS));
    };
    timer = setTimeout(tick, 700);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [open, soundEnabled, playPing]);

  // The closest in-range trace is automatically the scan candidate
  const inRange = liveTraces.find((t) => t.distance_m <= SCAN_RADIUS_M);

  // True between press and release. The players lookup below is a network
  // round-trip, so without this a quick tap could release BEFORE the response
  // landed and still arm a scan that then ran to completion on its own — the
  // one thing "HOLD TO SCAN" is meant to prevent. It also swallows the
  // synthetic mousedown that follows a touchstart on mobile.
  const holdingRef = useRef(false);

  const startScan = async () => {
    if (!inRange || scanState || holdingRef.current) return;
    holdingRef.current = true;
    // Ask backend how many players are at the trace to compute scan duration
    let players = 1;
    try {
      const r = await axios.get(`${API}/traces/${inRange.id}/players`);
      players = Math.max(1, r.data.players || 1);
    } catch {}
    if (!holdingRef.current) return;   // released while we were asking
    const seconds = 1 + Math.random() * (players - 1);  // [1 .. players)
    const durationMs = seconds * 1000;
    setScanState({ traceId: inRange.id, startedAt: Date.now(), durationMs, players });
  };

  const cancelScan = () => {
    holdingRef.current = false;
    if (scanRafRef.current) cancelAnimationFrame(scanRafRef.current);
    setScanState(null);
    setScanProgress(0);
  };

  // Drive scan completion
  useEffect(() => {
    if (!scanState) return undefined;
    let cancelled = false;
    const finish = async () => {
      if (cancelled) return;
      try {
        const r = await axios.post(`${API}/traces/scan`, {
          watcher_id: watcherId,
          trace_id: scanState.traceId,
          lat: userLocation.lat,
          lng: userLocation.lng,
        });
        if (r.data.success) {
          setScansCount(r.data.scans_count || (scansCount + 1));
          toast.success(`TRACE ACQUIRED · ${r.data.trace.agent_code}`, {
            description: `Total scans: ${r.data.scans_count}`,
          });
          if (onScanned) onScanned(r.data.trace.id);
          setTraces((prev) => prev.filter((t) => t.id !== scanState.traceId));
        } else if (r.data.reason === "already_scanned") {
          toast.error("Beaten to it — someone else got there first");
          setTraces((prev) => prev.filter((t) => t.id !== scanState.traceId));
        } else if (r.data.reason === "out_of_range") {
          toast.error(`Out of range — move ${Math.round(r.data.distance_m)} m closer`);
        } else {
          toast.error("Scan failed");
        }
      } catch {
        toast.error("Scan network error");
      } finally {
        holdingRef.current = false;
        setScanState(null);
        setScanProgress(0);
      }
    };
    const t = setTimeout(finish, scanState.durationMs);
    // Repaint the ring every frame for the length of the hold. The loop has to
    // push progress into state — a value derived at render time never updates,
    // because a held button produces no other renders.
    const raf = () => {
      const p = Math.min(1, (Date.now() - scanState.startedAt) / scanState.durationMs);
      setScanProgress(p);
      if (p < 1) scanRafRef.current = requestAnimationFrame(raf);
    };
    scanRafRef.current = requestAnimationFrame(raf);
    return () => {
      cancelled = true;
      clearTimeout(t);
      if (scanRafRef.current) cancelAnimationFrame(scanRafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanState]);

  if (!open) return null;

  return (
    <div className="trace-screen" data-testid="trace-tracker-screen">
      <div className="trace-screen-header">
        <button className="trace-back-btn" onClick={onClose} data-testid="trace-back-btn">
          <ArrowLeft size={14} />
          <span>BACK</span>
        </button>
        <div className="trace-screen-title">
          <RadarIcon size={14} />
          <span>TRACE TRACKER</span>
        </div>
        <div className="trace-screen-score" data-testid="trace-scans-count">
          <Trophy size={12} /> {scansCount}
        </div>
      </div>

      <div className="trace-screen-body">
        <div className="trace-radar-wrap">
          <HalfRadar
            traces={liveTraces}
            heading={heading}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
          <RadarBlipsOverlay
            traces={liveTraces}
            heading={heading}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
          <div className="trace-heading-tag" data-testid="trace-heading">
            <Compass size={11} /> {cardinal(heading)} · {Math.round(heading)}°
          </div>
        </div>

        {/* The ping's visible twin. Without it the getting-warmer cue would be
            audible only, which is no use with sound off or no hearing. */}
        {Number.isFinite(nearestDistance) && (
          <div className="proximity-meter" data-testid="proximity-meter">
            <span className="proximity-label">PROXIMITY</span>
            <div className="proximity-track">
              <div
                className={`proximity-fill ${nearestDistance <= SCAN_RADIUS_M ? "locked" : ""}`}
                style={{
                  width: `${Math.min(100, Math.max(0,
                    (1 - (nearestDistance - SCAN_RADIUS_M) / (PING_MAX_RANGE_M - SCAN_RADIUS_M)) * 100
                  ))}%`,
                }}
              />
            </div>
            <span className="proximity-dist">
              {nearestDistance <= SCAN_RADIUS_M ? "IN RANGE" : formatDistance(nearestDistance)}
            </span>
          </div>
        )}

        {orientationStatus === "unknown" && typeof DeviceOrientationEvent !== "undefined"
          && typeof DeviceOrientationEvent.requestPermission === "function" && (
            <button className="compass-request-btn" onClick={requestCompass} data-testid="compass-request-btn">
              Enable compass
            </button>
          )}
        {orientationStatus === "denied" && (
          <div className="trace-note">Compass denied — radar shows raw bearings (north-up).</div>
        )}
        {orientationStatus === "unavailable" && (
          <div className="trace-note">Compass unavailable on this device — radar shows raw bearings (north-up).</div>
        )}

        <div className="trace-list" data-testid="trace-list">
          {liveTraces.length === 0 && (
            <div className="empty-state">No trace signals in range yet — wait for an agent to visit a POI.</div>
          )}
          {liveTraces.map((t) => {
            const rel = ((t.bearing - heading) + 540) % 360 - 180;
            const inScanRange = t.distance_m <= SCAN_RADIUS_M;
            return (
              <button
                key={t.id}
                className={`trace-row ${selectedId === t.id ? "selected" : ""} ${inScanRange ? "in-range" : ""}`}
                onClick={() => setSelectedId(t.id)}
                data-testid={`trace-row-${t.id}`}
              >
                <span className="trace-row-code">{t.agent_code}</span>
                <span className="trace-row-dir">{cardinal(t.bearing)} · {instructionFor(rel)}</span>
                <span className="trace-row-dist">{formatDistance(t.distance_m)}</span>
              </button>
            );
          })}
        </div>

        {inRange && (
          <div className="scan-pad" data-testid="scan-pad">
            <button
              className={`scan-btn ${scanState ? "scanning" : ""}`}
              onMouseDown={startScan}
              onTouchStart={startScan}
              onMouseUp={cancelScan}
              onMouseLeave={cancelScan}
              onTouchEnd={cancelScan}
              data-testid="scan-btn"
            >
              <span className="scan-btn-label">
                {scanState ? "SCANNING…" : "HOLD TO SCAN"}
              </span>
              {scanState && (
                <svg className="scan-ring" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="46" fill="none"
                          stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
                  <circle cx="50" cy="50" r="46" fill="none"
                          stroke="#ffffff" strokeWidth="3"
                          strokeDasharray={2 * Math.PI * 46}
                          strokeDashoffset={2 * Math.PI * 46 * (1 - scanProgress)}
                          transform="rotate(-90 50 50)"
                          strokeLinecap="round" />
                </svg>
              )}
            </button>
            <div className="scan-meta">
              {inRange.agent_code} trace acquired · signal locked
              {scanState && ` · ${scanState.players} player${scanState.players === 1 ? "" : "s"} here`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
